import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { create } from "zustand";
import type { CssEvals } from "@/components/Canvas/Storyboard/StaticIframe/hooks/useExtractCss";
import { isNavTreeEmpty } from "@/components/Canvas/Storyboard/StaticIframe/utils/navTreeLookupUtils";
import { STORYBOARD_TYPES } from "@/utils/constants";
import type { Database } from "@/utils/supabase.types";
import { TempoElement } from "@/utils/tempoElement";
import type { NavTreeNode, Storyboard } from "@/utils/types";
import { useActiveCanvas } from "@/utils/utils";
import { useCodebaseStore } from "../../../store/zustand/codebaseStore";

export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 256;
export const CREATE_NEW_IFRAME = "CREATE_NEW_IFRAME";

interface CanvasPos {
  zoomPerc?: number;
  transformX?: number;
  transformY?: number;
}

// Viewport center in canvas coordinates (the point at the center of the visible viewport)
export interface ViewportCenter {
  x: number;
  y: number;
}

type MachineForCanvas =
  Database["public"]["Tables"]["deployment_machines"]["Row"];

export interface CanvasStore {
  // Consumers should always use useCanvasPos, which is a debounced mirror of
  // canvasIdToPosRealtime, in order to avoid adding lag to panning/zooming.
  canvasIdToPos: {
    [canvasId: string]: CanvasPos;
  };
  canvasIdToPosRealtime: {
    // Should only be used by the Canvas rendering
    [canvasId: string]: CanvasPos;
  };

  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;

  setCanvasPosValue: (canvasId: string, values: Partial<CanvasPos>) => void;

  // This Updates _very_ frequently, so do not put it in any render loops
  canvasMousePos: {
    x: number;
    y: number;
  };

  setCanvasMousePos: (x: number, y: number) => void;

  elementKeyToNavNodeLookup: { [elementKey: string]: NavTreeNode };
  sbIdToCodebaseIdToNavNodeLookup: {
    [storyboardId: string]: { [codebaseId: string]: NavTreeNode[] };
  };
  storyboardToNavTree: { [storyboardId: string]: NavTreeNode };
  domUniquePathElementKeyLookup: { [domUniquePath: string]: NavTreeNode };

  // For each storyboard, which codebase Ids were last live in it so we know when they change
  storyboardIdToCodebaseIds: { [storyboardId: string]: Set<string> };
  storyboardIdToFilepaths: { [storyboardId: string]: Set<string> };
  // For each storyboard, which codebaseId is the actual Story component that's being rendered
  storyboardIdToRootCodebaseId: { [storyboardId: string]: string };

  // Temporary storyboard for element drag-out (not persisted)
  temporaryStoryboard: Storyboard | null;
  isDragging: boolean;
  isDraggingAbsolute: boolean;
  draggingPosition: string | null;

  setElementKeyToNavNodeLookup: (
    storyboardId: string,
    elementKeyToNavNodeLookup: {
      [elementKey: string]: NavTreeNode;
    },
  ) => void;
  setDomUniquePathElementKeyLookup: (
    storyboardId: string,
    domUniquePathElementKeyLookup: {
      [domUniquePath: string]: NavTreeNode;
    },
  ) => void;
  setCodebaseIdToNavNodeLookup: (
    storyboardId: string,
    codebaseIdToNavNodeLookup: {
      [codebaseId: string]: NavTreeNode[];
    },
  ) => void;
  setNavTreeForStoryboard: (
    storyboardId: string,
    navTree: NavTreeNode | null,
  ) => void;
  clearNavTreeForStoryboard: (storyboardId: string) => void;
  setStoryboardIdToCodebaseIds: (
    storyboardId: string,
    codebaseIds?: Set<string>,
  ) => void;
  setStoryboardIdToRootCodebaseId: (
    storyboardId: string,
    rootCodebaseId: string,
  ) => void;

  // Temporary storyboard management
  setTemporaryStoryboardForDrag: (
    storyboard: any | null,
    isDraggingAbsolute?: boolean,
    draggingPosition?: string,
  ) => void;

  menuLeft: number | null;
  menuTop: number | null;
  setMenu: (menuLeft: number, menuTop: number) => void;

  canvasComponentKey: string | null;
  setCanvasComponentKey: (canvasComponentKey: string) => void;

  nodeOpenDict: { [nodeId: string]: boolean };
  setNodeOpenDict: (nodeOpenDict: { [nodeId: string]: boolean }) => void;
  onClickNodeOpen: (tempoElement: TempoElement) => void;

  resetCanvasStore: () => void;

  triggerDeleteElement: boolean;
  setTriggerDeleteElement: (triggerDeleteElement: boolean) => void;

  usersForProject: { [projectId: string]: any[] };
  setUsersForProject: (projectId: string, users: any[]) => void;

  convexLoginUrl: string;
  setConvexLoginUrl: (convexLoginUrl: string) => void;

  expoQrCodeUrl: string;
  setExpoQrCodeUrl: (expoQrCodeUrl: string) => void;

  // Story generation loading state
  isGeneratingStories: boolean;
  setIsGeneratingStories: (isGenerating: boolean) => void;

  // Story generation status tracking
  storyGenerationStatus: {
    phase: "idle" | "generating" | "awaiting-approval" | "creating" | "warning";
    message?: string;
  };
  setStoryGenerationStatus: (status: {
    phase: "idle" | "generating" | "awaiting-approval" | "creating" | "warning";
    message?: string;
  }) => void;

  // Iframe reuse management
  iframeAssignments: { [storyboardId: string]: string }; // maps storyboard id to iframe id or "CREATE_NEW"
  setIframeAssignment: (storyboardId: string, iframeId: string) => void;

  storyboardIdToType: { [storyboardId: string]: STORYBOARD_TYPES };
  setStoryboardIdToTypes: (storyboardIdToType: {
    [storyboardId: string]: STORYBOARD_TYPES;
  }) => void;

  selectedElementCssEvals: CssEvals | null;
  selectedElementDomClasses: string[] | null;
  setSelectedElementCssEvals: (
    selectedElementCssEvals: CssEvals | undefined,
    selectedElementDomClasses: string[] | undefined,
  ) => void;

  // To be added to the iframes
  temporaryFiles: {
    id: string;
    file: File;
  }[];
  addTemporaryFile: (temporaryFiles: { id: string; file: File }) => void;
  removeTemporaryFile: (id: string) => void;
  clearTemporaryFiles: () => void;

  // Drop position tracking for element insertion
  dropPosition: {
    beforeCodebaseId?: string;
    afterCodebaseId?: string;
    index?: number;
  } | null;
  dropCoordinatesInSb: {
    x: number;
    y: number;
  } | null;
  dropCoordinatesForAbsolute: {
    x: number;
    y: number;
  } | null;

  setDropPosition: (newInfo: {
    position?: {
      beforeCodebaseId?: string;
      afterCodebaseId?: string;
      index?: number;
    } | null;
    dropCoordinatesInSb?: {
      x: number;
      y: number;
    } | null;
    dropCoordinatesForAbsolute?: {
      x: number;
      y: number;
    } | null;
  }) => void;

  queryStoryboardContentSize: (
    storyboardId: string,
  ) => null | { width: number; height: number };
  setQueryStoryboardContentSize: (
    query: (storyboardId: string) => null | { width: number; height: number },
  ) => void;
}

const setCanvasPosValueRealtime = (
  get,
  set,
  canvasId: string,
  values: Partial<CanvasPos>,
) => {
  const currentPositions = get().canvasIdToPosRealtime;
  if (!currentPositions[canvasId]) {
    currentPositions[canvasId] = {
      zoomPerc: 1,
      transformX: 0,
      transformY: 0,
    };
  }
  Object.keys(values).forEach((key) => {
    currentPositions[canvasId][key] = values[key];
  });
  set({
    canvasIdToPosRealtime: {
      ...currentPositions,
    },
  });
};

const updateCanvasPosValueThrottled = debounce(
  (get, set, canvasId: string) => {
    const realtimeValues = get().canvasIdToPosRealtime;
    set({
      canvasIdToPos: realtimeValues,
    });
  },
  100,
  { leading: true, trailing: true },
);

export const useCanvasStore = create<CanvasStore>((set, get) => ({
  canvasIdToPos: {},
  canvasIdToPosRealtime: {},

  isLoading: false,
  setIsLoading: (isLoading: boolean) => set({ isLoading }),

  canvasMousePos: {
    x: 0,
    y: 0,
  },

  setCanvasMousePos: (x: number, y: number) => {
    set({ canvasMousePos: { x, y } });
  },

  setCanvasPosValue: (canvasId: string, values: Partial<CanvasPos>) => {
    const zoomPercToSet = values["zoomPerc"];

    if (zoomPercToSet !== undefined) {
      if (zoomPercToSet < MIN_ZOOM) {
        values["zoomPerc"] = MIN_ZOOM;
      } else if (zoomPercToSet > MAX_ZOOM) {
        values["zoomPerc"] = MAX_ZOOM;
      }
    }

    setCanvasPosValueRealtime(get, set, canvasId, values);
    updateCanvasPosValueThrottled(get, set, canvasId); // Follows the realtime value
  },

  storyboardIdToType: {},
  setStoryboardIdToTypes: (storyboardIdToType: {
    [storyboardId: string]: STORYBOARD_TYPES;
  }) => {
    set((state) => ({
      storyboardIdToType,
    }));
  },

  elementKeyToNavNodeLookup: {},
  codebaseIdToNavNodeLookup: {},
  storyboardToNavTree: {},
  domUniquePathElementKeyLookup: {},

  // Temporary storyboard for element drag-out
  temporaryStoryboard: null,
  isDraggingAbsolute: false,
  setElementKeyToNavNodeLookup: (
    storyboardId: string,
    elementKeyToNavNodeLookup: {
      [elementKey: string]: NavTreeNode;
    },
  ) => {
    // Delete any keys that contain the storyboardId
    const newState = { ...get().elementKeyToNavNodeLookup };
    Object.keys(newState).forEach((key) => {
      if (key.includes(storyboardId)) {
        delete newState[key];
      }
    });

    set({
      elementKeyToNavNodeLookup: {
        ...newState,
        ...elementKeyToNavNodeLookup,
      },
    });
  },
  setDomUniquePathElementKeyLookup: (
    storyboardId: string,
    domUniquePathElementKeyLookup: {
      [domUniquePath: string]: NavTreeNode;
    },
  ) => {
    // Delete any keys that contain the storyboardId
    const newState = { ...get().domUniquePathElementKeyLookup };
    Object.keys(newState).forEach((key) => {
      if (key.includes(storyboardId)) {
        delete newState[key];
      }
    });

    set({
      domUniquePathElementKeyLookup: {
        ...newState,
        ...domUniquePathElementKeyLookup,
      },
    });
  },
  setCodebaseIdToNavNodeLookup: (
    storyboardId: string,
    codebaseIdToNavNodeLookup: {
      [codebaseId: string]: NavTreeNode[];
    },
  ) => {
    set((state) => ({
      sbIdToCodebaseIdToNavNodeLookup: {
        ...state.sbIdToCodebaseIdToNavNodeLookup,
        [storyboardId]: codebaseIdToNavNodeLookup,
      },
    }));
  },
  setNavTreeForStoryboard: (storyboardId: string, navTree: NavTreeNode) => {
    set((state) => ({
      storyboardToNavTree: {
        ...state.storyboardToNavTree,
        [storyboardId]: navTree,
      },
    }));
  },
  clearNavTreeForStoryboard: (storyboardId: string) => {
    const storyboardToNavTree = get().storyboardToNavTree;
    delete storyboardToNavTree[storyboardId];
    set({ storyboardToNavTree });
  },
  setStoryboardIdToCodebaseIds: (
    storyboardId: string,
    codebaseIds?: Set<string>,
  ) => {
    if (!codebaseIds) {
      const newState = {
        ...get().storyboardIdToCodebaseIds,
      };
      delete newState[storyboardId];

      const newFilepaths = {
        ...get().storyboardIdToFilepaths,
      };
      delete newFilepaths[storyboardId];

      set({
        storyboardIdToCodebaseIds: newState,
        storyboardIdToFilepaths: newFilepaths,
      });
      return;
    }

    const treeElements = useCodebaseStore.getState().treeElementLookup;
    const filepaths = new Set(
      Array.from(codebaseIds).map(
        (codebaseId) => treeElements[codebaseId]?.filename,
      ),
    );

    set((state) => ({
      storyboardIdToCodebaseIds: {
        ...state.storyboardIdToCodebaseIds,
        [storyboardId]: codebaseIds,
      },
      storyboardIdToFilepaths: {
        ...state.storyboardIdToFilepaths,
        [storyboardId]: filepaths,
      },
    }));
  },

  storyboardIdToRootCodebaseId: {},
  setStoryboardIdToRootCodebaseId: (
    storyboardId: string,
    rootCodebaseId: string,
  ) => {
    set((state) => ({
      storyboardIdToRootCodebaseId: {
        ...state.storyboardIdToRootCodebaseId,
        [storyboardId]: rootCodebaseId,
      },
    }));
  },

  // Temporary storyboard management
  setTemporaryStoryboardForDrag: (
    storyboard: any | null,
    isDraggingAbsolute?: boolean,
    draggingPosition?: string,
  ) =>
    set({
      isDragging: Boolean(storyboard),
      temporaryStoryboard: storyboard,
      isDraggingAbsolute: isDraggingAbsolute ?? false,
      draggingPosition: draggingPosition ?? null,
    }),

  menuLeft: null,
  menuTop: null,
  setMenu: (menuLeft: number, menuTop: number) => set({ menuLeft, menuTop }),

  canvasComponentKey: null,
  setCanvasComponentKey: (canvasComponentKey: string) =>
    set({ canvasComponentKey }),

  nodeOpenDict: { root: true },
  setNodeOpenDict: (nodeOpenDict: { [nodeId: string]: boolean }) => {
    set({ nodeOpenDict });
  },
  onClickNodeOpen: (tempoElement: TempoElement) => {
    set((state) => ({
      nodeOpenDict: {
        ...state.nodeOpenDict,
        [tempoElement.getKey()]: !state.nodeOpenDict[tempoElement.getKey()],
      },
    }));
  },

  resetCanvasStore: () => {
    set({
      canvasIdToPos: {},
      canvasIdToPosRealtime: {},
      canvasMousePos: {
        x: 0,
        y: 0,
      },
      elementKeyToNavNodeLookup: {},
      domUniquePathElementKeyLookup: {},
      sbIdToCodebaseIdToNavNodeLookup: {},
      storyboardToNavTree: {},

      menuLeft: null,
      menuTop: null,
      canvasComponentKey: null,
      nodeOpenDict: { root: true },
      usersForProject: {},
      convexLoginUrl: undefined,
      expoQrCodeUrl: undefined,
      storyboardIdToCodebaseIds: {},
      storyboardIdToFilepaths: {},
      storyboardIdToRootCodebaseId: {},
      iframeAssignments: {},
      temporaryFiles: [],
      selectedElementCssEvals: null,
    });
  },

  triggerDeleteElement: false,
  setTriggerDeleteElement: (triggerDeleteElement: boolean) =>
    set({ triggerDeleteElement }),

  usersForProject: {},
  setUsersForProject: (projectId: string, users: any[]) => {
    set((state) => ({
      usersForProject: {
        ...state.usersForProject,
        [projectId]: users,
      },
    }));
  },

  // @ts-expect-error TS2322
  convexLoginUrl: null,
  setConvexLoginUrl: (convexLoginUrl: string) => {
    set({ convexLoginUrl });
  },

  // @ts-expect-error TS2322
  expoQrCodeUrl: null,
  setExpoQrCodeUrl: (expoQrCodeUrl: string) => {
    set({ expoQrCodeUrl });
  },

  isGeneratingStories: false,
  setIsGeneratingStories: (isGenerating: boolean) =>
    set({ isGeneratingStories: isGenerating }),

  storyGenerationStatus: { phase: "idle" },
  setStoryGenerationStatus: (status) => set({ storyGenerationStatus: status }),

  iframeAssignments: {},
  setIframeAssignment: (storyboardId: string, iframeId: string) => {
    const newIframeAssignments = {
      ...get().iframeAssignments,
    };

    // Find any other storyboards that use this iframe and remove the assignment
    Object.keys(newIframeAssignments).forEach((sbId) => {
      if (newIframeAssignments[sbId] === iframeId) {
        delete newIframeAssignments[sbId];
      }
    });

    set((state) => ({
      iframeAssignments: {
        ...newIframeAssignments,
        [storyboardId]: iframeId,
      },
    }));
  },

  selectedElementCssEvals: null,
  selectedElementDomClasses: null,
  setSelectedElementCssEvals: (
    selectedElementCssEvals: CssEvals | undefined,
    selectedElementDomClasses: string[] | undefined,
  ) => set({ selectedElementCssEvals, selectedElementDomClasses }),

  temporaryFiles: [],
  addTemporaryFile: (temporaryFile: { id: string; file: File }) =>
    set((state) => ({
      temporaryFiles: [...state.temporaryFiles, temporaryFile],
    })),
  removeTemporaryFile: (id: string) =>
    set((state) => ({
      temporaryFiles: state.temporaryFiles.filter((file) => file.id !== id),
    })),
  clearTemporaryFiles: () => set({ temporaryFiles: [] }),

  dropPosition: null,
  dropCoordinatesInSb: null,
  dropCoordinatesForAbsolute: null,

  setDropPosition: ({
    position,
    dropCoordinatesInSb,
    dropCoordinatesForAbsolute,
  }) =>
    set({
      dropPosition: position,
      dropCoordinatesInSb: dropCoordinatesInSb,
      dropCoordinatesForAbsolute: dropCoordinatesForAbsolute,
    }),

  queryStoryboardContentSize: () => null,
  setQueryStoryboardContentSize: (query) =>
    set({ queryStoryboardContentSize: query }),
}));

export const useHoveredNavTreeNode = (): {
  hoveredNavTreeNode: NavTreeNode | null;
  hoveredElement: TempoElement;
  hoveredElementKey: string;
  storyboardIdHovered: string | null;
} => {
  const hoveredElementKey = useSelector(
    (state: any) => state.iframe.hoveredElementKey,
  );

  const elementKeyToNavNodeLookup = useCanvasStore(
    (state) => state.elementKeyToNavNodeLookup,
  );

  const storyboardToNavTree = useCanvasStore(
    (state) => state.storyboardToNavTree,
  );

  const storyboardIdToType = useCanvasStore(
    (state) => state.storyboardIdToType,
  );

  // Save the selected nav tree node & multiselected nodes to use inside other components
  const hoveredNavTreeNode = useMemo(() => {
    const hoveredElement = TempoElement.fromKey(hoveredElementKey);
    if (hoveredElement?.isStoryboard()) {
      const storyboardType = storyboardIdToType[hoveredElement?.storyboardId];
      if (storyboardType && storyboardType !== STORYBOARD_TYPES.APPLICATION) {
        const navTree =
          storyboardToNavTree[hoveredElement?.storyboardId] || null;
        if (isNavTreeEmpty(navTree)) {
          return null;
        }

        return navTree;
      }

      return null;
    }

    return elementKeyToNavNodeLookup[hoveredElement?.getKey()] || null;
  }, [
    hoveredElementKey,
    elementKeyToNavNodeLookup,
    storyboardIdToType,
    storyboardToNavTree,
  ]);

  const hoveredElement = useMemo(() => {
    return (
      hoveredNavTreeNode?.tempoElement ??
      (hoveredElementKey
        ? TempoElement.fromKey(hoveredElementKey)
        : TempoElement.empty())
    );
  }, [hoveredNavTreeNode, hoveredElementKey]);

  const hoveredElementKeyToReturn = useMemo(() => {
    return hoveredElement.getKey();
  }, [hoveredElement]);

  // Take directly from the element key so that it's not affected by the flat framer storyboards
  const storyboardIdHovered = useMemo(() => {
    const element = TempoElement.fromKey(hoveredElementKey);
    return element.isStoryboard() ? element.storyboardId : null;
  }, [hoveredElementKey]);

  return {
    hoveredNavTreeNode,
    hoveredElement,
    hoveredElementKey: hoveredElementKeyToReturn,
    storyboardIdHovered,
  };
};

export const useSelectedNavTreeNode = (): {
  selectedNavTreeNode: NavTreeNode | null;
  selectedElement: TempoElement;
  selectedElementKey: string;
  storyboardIdSelected: string | null;
} => {
  const selectedElementKey = useSelector(
    (state: any) => state.iframe.selectedElementKey,
  );

  const elementKeyToNavNodeLookup = useCanvasStore(
    (state) => state.elementKeyToNavNodeLookup,
  );

  const storyboardToNavTree = useCanvasStore(
    (state) => state.storyboardToNavTree,
  );

  const storyboardIdToType = useCanvasStore(
    (state) => state.storyboardIdToType,
  );

  // Save the selected nav tree node & multiselected nodes to use inside other components
  const selectedNavTreeNode = useMemo(() => {
    const selectedElement = TempoElement.fromKey(selectedElementKey);

    if (selectedElement?.isStoryboard()) {
      const storyboardType = storyboardIdToType[selectedElement?.storyboardId];
      if (storyboardType && storyboardType !== STORYBOARD_TYPES.APPLICATION) {
        const navTree =
          storyboardToNavTree[selectedElement?.storyboardId] || null;
        if (isNavTreeEmpty(navTree)) {
          return null;
        }

        return navTree;
      }

      return null;
    }

    return elementKeyToNavNodeLookup[selectedElement?.getKey()] || null;
  }, [
    selectedElementKey,
    elementKeyToNavNodeLookup,
    storyboardIdToType,
    storyboardToNavTree,
  ]);

  const selectedElement = useMemo(() => {
    return (
      selectedNavTreeNode?.tempoElement ??
      (selectedElementKey
        ? TempoElement.fromKey(selectedElementKey)
        : TempoElement.empty())
    );
  }, [selectedNavTreeNode, selectedElementKey]);

  const returnSelectedElementKey = useMemo(() => {
    return selectedElement.getKey();
  }, [selectedElement]);

  // Take directly from the element key so that it's not affected by the flat framer storyboards
  const storyboardIdSelected = useMemo(() => {
    const element = TempoElement.fromKey(selectedElementKey);
    return element.isStoryboard() ? element.storyboardId : null;
  }, [selectedElementKey]);

  return {
    selectedNavTreeNode,
    selectedElement,
    selectedElementKey: returnSelectedElementKey,
    storyboardIdSelected,
  };
};

export const useMultiselectedNavTreeNode = (): {
  multiselectedNavTreeNodes: NavTreeNode[];
  multiselectedElements: TempoElement[];
  multiselectedElementKeys: string[];
  multiselectedStoryboardIds: string[];
} => {
  const multiselectedElementKeys = useSelector(
    (state: any) => state.iframe.multiSelectedElementKeys,
  );

  const elementKeyToNavNodeLookup = useCanvasStore(
    (state) => state.elementKeyToNavNodeLookup,
  );

  const storyboardToNavTree = useCanvasStore(
    (state) => state.storyboardToNavTree,
  );

  const storyboardIdToType = useCanvasStore(
    (state) => state.storyboardIdToType,
  );

  // Save the selected nav tree node & multiselected nodes to use inside other components
  const multiselectedNavTreeNodes = useMemo(() => {
    if (!multiselectedElementKeys?.length) {
      return [];
    }

    return multiselectedElementKeys
      .map((elementKey) => {
        const element = TempoElement.fromKey(elementKey);

        if (element.isStoryboard()) {
          const storyboardType = storyboardIdToType[element?.storyboardId];
          if (
            storyboardType &&
            storyboardType !== STORYBOARD_TYPES.APPLICATION
          ) {
            const navTree = storyboardToNavTree[element?.storyboardId] || null;
            if (isNavTreeEmpty(navTree)) {
              return null;
            }

            return navTree;
          }

          return null;
        }

        return elementKeyToNavNodeLookup[elementKey];
      })
      .filter(Boolean);
  }, [
    multiselectedElementKeys,
    elementKeyToNavNodeLookup,
    storyboardToNavTree,
    storyboardIdToType,
  ]);

  const multiselectedElements: TempoElement[] = useMemo(() => {
    const elements = multiselectedNavTreeNodes
      .map((node) => node.tempoElement)
      .filter(Boolean);
    if (elements.length === 0 && multiselectedElementKeys?.length) {
      return multiselectedElementKeys.map((elementKey) =>
        TempoElement.fromKey(elementKey),
      );
    }

    return elements;
  }, [multiselectedNavTreeNodes, multiselectedElementKeys]);

  const multiselectedElementKeysToReturn: string[] = useMemo(
    () =>
      multiselectedNavTreeNodes
        .map((node) => node.tempoElement.getKey())
        .filter(Boolean) ?? [],
    [multiselectedNavTreeNodes],
  );

  // Take directly from the element key so that it's not affected by the flat framer storyboards
  const multiselectedStoryboardIds = useMemo(() => {
    const elements =
      multiselectedElementKeys?.map((elementKey) =>
        TempoElement.fromKey(elementKey),
      ) ?? [];
    return elements
      .map((element) => (element.isStoryboard() ? element.storyboardId : null))
      .filter(Boolean);
  }, [multiselectedElementKeys]);

  return {
    multiselectedNavTreeNodes,
    multiselectedElements,
    multiselectedElementKeys: multiselectedElementKeysToReturn,
    multiselectedStoryboardIds,
  };
};

export const useCanvasPos = (): CanvasPos => {
  const activeCanvas = useActiveCanvas();

  const canvasIdToPos = useCanvasStore((state) => state.canvasIdToPos);
  return useMemo(() => {
    return activeCanvas?.id
      ? canvasIdToPos?.[activeCanvas?.id] ||
          // canvasIdToPos might not've been set yet if its still debounced
          useCanvasStore.getState().canvasIdToPosRealtime[activeCanvas?.id] ||
          {}
      : {};
  }, [activeCanvas?.id, canvasIdToPos]);
};

export const useUnzoomMultiplier = (): number => {
  const { zoomPerc } = useCanvasPos();
  return 1 / (zoomPerc || 1);
};

// Realtime version that updates every frame during zoom - use for UI elements
// that need to stay correctly sized during continuous zooming (e.g. resize handles)
export const useUnzoomMultiplierRealtime = (): number => {
  const zoomPerc = useCanvasZoomRealtime();
  return 1 / (zoomPerc || 1);
};

// Do not use this unless you need to smoothly react to the value every frame.
export const useCanvasPosRealtime = (): CanvasPos => {
  const activeCanvas = useActiveCanvas();

  const canvasIdToPosRealtime = useCanvasStore(
    (state) => state.canvasIdToPosRealtime,
  );

  return useMemo(() => {
    return activeCanvas?.id
      ? canvasIdToPosRealtime?.[activeCanvas?.id] || {}
      : {};
  }, [activeCanvas?.id, canvasIdToPosRealtime]);
};

export const useCanvasZoomRealtime = (): number => {
  const activeCanvas = useActiveCanvas();
  const zoomPerc = useCanvasStore(
    (state) =>
      (activeCanvas?.id &&
        state.canvasIdToPosRealtime[activeCanvas.id]?.zoomPerc) ||
      1,
  );

  return zoomPerc;
};

export const isCanvasPosInitialized = (
  canvasPos: CanvasPos,
): canvasPos is Required<CanvasPos> => {
  return (
    canvasPos !== undefined &&
    canvasPos.transformX !== undefined &&
    canvasPos.transformY !== undefined &&
    Boolean(canvasPos.zoomPerc)
  );
};

// Default viewport center when not yet initialized
const DEFAULT_VIEWPORT_CENTER: ViewportCenter = { x: 0, y: 0 };

/**
 * Get the current viewport center in canvas coordinates (debounced).
 * This is the exact center point of what's currently visible on screen.
 * Computed from: { x: -transformX / zoomPerc, y: -transformY / zoomPerc }
 */
export const useViewportCenter = (): ViewportCenter => {
  const { transformX, transformY, zoomPerc } = useCanvasPos();

  return useMemo(() => {
    if (transformX === undefined || transformY === undefined || !zoomPerc) {
      return DEFAULT_VIEWPORT_CENTER;
    }
    return {
      x: -transformX / zoomPerc,
      y: -transformY / zoomPerc,
    };
  }, [transformX, transformY, zoomPerc]);
};

/**
 * Get the current viewport center in canvas coordinates (debounced).
 * Debounced with 500ms delay and 5 second maxWait.
 */
export const useViewportCenterDebounced = (): ViewportCenter => {
  const viewportCenter = useViewportCenter();
  const [debouncedCenter, setDebouncedCenter] =
    useState<ViewportCenter>(viewportCenter);

  const debouncedSetCenter = useRef(
    debounce(setDebouncedCenter, 500, { maxWait: 5000 }),
  ).current;

  useEffect(() => {
    debouncedSetCenter(viewportCenter);
  }, [viewportCenter, debouncedSetCenter]);

  useEffect(() => {
    return () => {
      debouncedSetCenter.cancel();
    };
  }, [debouncedSetCenter]);

  return debouncedCenter;
};
